import {
  Box,
  Button,
  FieldText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Tabs,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import { formatInternational } from 'lib-shared-phone-numbers';
import { ampli } from 'lib-shared-tracking';
import { ChangeEvent, PureComponent } from 'react';
import { connect } from 'react-redux';

import type { AppDispatch } from 'bootstrap.store';
import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import {
  withAvailableCountries,
  WithAvailableCountriesInjectedProps,
  withPhoneNumbers,
  WithPhoneNumbersInjectedProps,
  withPhoneSearch,
  WithPhoneSearchInjectedProps,
} from 'hocs';
import type { Country } from 'hocs/countries/api/types';
import type {
  PhoneNumbersGroup,
  PhoneNumbersSearch,
} from 'hocs/telephony/api/types';
import { validateName } from 'utils/formValidator';

import CountrySelect from '../countrySelect';
import CustomTab from '../customTab';

import styles from './buyNumber.module.scss';

import '../telephony.less';

export type BuyNumberQueryT = {
  number?: PhoneNumbersSearch;
  group?: PhoneNumbersGroup;
};
export type TabT = 'local-area-numbers' | 'national-numbers';

type PropsT = {
  isOpen: boolean;
  hideSearchNumberModal: () => void;
  fetchNumbers: () => void;
} & WithPhoneSearchInjectedProps &
  WithPhoneNumbersInjectedProps &
  WithAvailableCountriesInjectedProps &
  BuyNumberDispatch;

type StateT = {
  showTabs: boolean;
  name: string;
  isConfirmGroupOpen: boolean;
  isConfirmNumberOpen: boolean;
  currentTab: TabT;
  selectedGroup: PhoneNumbersGroup | null;
  selectedNumber: PhoneNumbersSearch | null;
  selectedCountry: Country | null;
};

const TABS = [
  {
    id: 'local-area-numbers',
    label: 'Local Area Numbers',
  },
  {
    id: 'national-numbers',
    label: 'National Numbers',
  },
];

class BuyNumber extends PureComponent<PropsT, StateT> {
  state: StateT = {
    showTabs: false,
    name: '',
    isConfirmGroupOpen: false,
    isConfirmNumberOpen: false,
    currentTab: 'local-area-numbers',
    selectedGroup: null,
    selectedNumber: null,
    selectedCountry: null,
  };

  componentDidMount() {
    const {
      availableCountries: { getItems },
    } = this.props;

    getItems().then(
      ({
        data: {
          countries: [firstCountry],
        },
      }) => this.setSelectedCountry(firstCountry),
    );
  }

  @boundMethod
  onChangePhoneLabel(event?: ChangeEvent<HTMLInputElement>) {
    this.setState({ name: event?.target?.value ?? '' });
  }

  @boundMethod
  setSelectedCountry(selectedCountry: Country) {
    this.setState({ selectedCountry });
  }

  @boundMethod
  setShowTabs(showTabs: boolean) {
    const { numberCountrySelected } = this.props;

    numberCountrySelected();
    this.setState(({ showTabs: stateShowTabs }) => ({
      showTabs: !stateShowTabs && showTabs ? showTabs : stateShowTabs,
    }));
  }

  @boundMethod
  closeConfirmPurchase(isCancelled = true) {
    const { numberCountrySelected } = this.props;

    if (isCancelled) {
      ampli.phoneFormCancelled();
      // Go back to previous guide step.
      numberCountrySelected();
    }

    this.setState({
      isConfirmGroupOpen: false,
      isConfirmNumberOpen: false,
      selectedGroup: null,
      selectedNumber: null,
    });
  }

  @boundMethod
  handleConfirmPurchase(query: BuyNumberQueryT) {
    const { numberSelected } = this.props;
    const { group, number } = query;
    const isConfirmGroupOpen = !!group;
    const isConfirmNumberOpen = !!number;

    numberSelected();

    this.setState({
      isConfirmGroupOpen,
      isConfirmNumberOpen,
      selectedGroup: group ?? null,
      selectedNumber: number ?? null,
    });
  }

  @boundMethod
  handleTabChange(value: string) {
    const currentTab = value as TabT;

    this.setState({ currentTab });
  }

  @boundMethod
  handleBuyNumber() {
    const { selectedNumber: number, name } = this.state;
    const {
      phoneNumbers: { buyNumber },
    } = this.props;

    if (!number) {
      return;
    }

    ampli.phoneFormSubmitted();
    buyNumber({ number, name }).then(this.afterPurchase);
  }

  @boundMethod
  handleBuyGroup() {
    const { selectedGroup: group, name } = this.state;
    const {
      phoneNumbers: { buyGroup },
    } = this.props;

    if (!group) {
      return;
    }

    ampli.phoneFormSubmitted();
    buyGroup({ group, name }).then(this.afterPurchase);
  }

  @boundMethod
  handleModalClose() {
    const { numberBuyCanceled, hideSearchNumberModal } = this.props;

    numberBuyCanceled();
    hideSearchNumberModal();
  }

  @boundMethod
  afterPurchase() {
    const { fetchNumbers, numberBuyCanceled, hideSearchNumberModal } =
      this.props;

    /*
     * In future, we should pass `phoneNumbers`
     * down from phone page, as then we could call `phoneNumbers.getItems` directly.
     * For now, not to break the old phone page we must take `fetchNumbers` and use that.
     */
    fetchNumbers();
    hideSearchNumberModal();
    this.closeConfirmPurchase(false);

    /*
     * Close the guide.
     *
     * Normally we should call `numberPurchased`,
     * however, that part of the guide is outdated and needs reworking.
     */
    numberBuyCanceled();
  }

  @boundMethod
  renderTabs() {
    const { currentTab } = this.state;

    return (
      <Tabs
        tabs={TABS}
        selectedId={currentTab}
        onChange={this.handleTabChange}
        className="buy-number-modal__tabs"
      />
    );
  }

  renderCurrentTab() {
    const { currentTab } = this.state;
    const { phoneSearch } = this.props;

    switch (currentTab) {
      case 'local-area-numbers':
        return (
          <CustomTab
            type="geographic"
            title="Local Area Numbers"
            phoneSearch={phoneSearch}
            buyNumber={this.handleConfirmPurchase}
          />
        );

      case 'national-numbers':
        return (
          <CustomTab
            type="national"
            title="National Numbers"
            phoneSearch={phoneSearch}
            buyNumber={this.handleConfirmPurchase}
          />
        );

      default:
        return null;
    }
  }

  renderNameField() {
    const { name: value } = this.state;

    return (
      <FieldText
        placeholder="E.g. Support (UK), Support (US), etc."
        className={styles.confirmInput}
        label="Add a name"
        meta={{
          error: validateName(value),
        }}
        input={{
          value,
          onChange: this.onChangePhoneLabel,
          name: 'name',
        }}
      />
    );
  }

  isNameValid() {
    const { name: nameValue } = this.state;
    return validateName(nameValue) === undefined;
  }

  renderBuyGroupModal() {
    const { currentTab, selectedGroup } = this.state;
    const type =
      currentTab === 'local-area-numbers' ? 'geographic' : 'national';

    const {
      phoneNumbers: { purchaseAsyncStatus },
      phoneSearch: {
        items: { [type]: items },
      },
    } = this.props;

    if (!selectedGroup) {
      return null;
    }

    const { address_requirement } = selectedGroup;
    const isLoading = purchaseAsyncStatus !== 'succeeded';
    const groupCountry = items?.country;
    const addressRequirement = address_requirement?.requirement_type;
    const isAddressRequired =
      !!addressRequirement && addressRequirement !== 'worldwide';

    return (
      <ConfirmModal
        isShown={true}
        headline="Buy a number"
        confirmText="Buy number"
        confirmVariant="primary"
        confirmProps={{ isLoading, disabled: !this.isNameValid() }}
        confirm={this.handleBuyGroup}
        cancelText="Cancel"
        cancel={this.closeConfirmPurchase}
      >
        <div className={styles.confirm}>
          <div className={styles.confirmText}>
            You are about to purchase an area phone number with the following
            details:
          </div>
          <dl
            className={classnames('dl-horizontal', styles.confirmRequirements)}
          >
            <dt>Country:</dt>
            <dd>
              <span className="text-capitalize">
                {groupCountry?.country?.toLowerCase() || ''}
              </span>
            </dd>
            <dt>City:</dt>
            <dd>
              {selectedGroup && (
                <span className="text-capitalize">
                  {selectedGroup.city_name?.toLowerCase() || ''}
                </span>
              )}
            </dd>
            <dt>Starting with:</dt>
            <dd>
              <span>
                (+
                {groupCountry && groupCountry.country_prefix}){' '}
                {selectedGroup?.area_code && `${selectedGroup.area_code}...`}
              </span>
            </dd>
            {isAddressRequired && (
              <>
                <dt>Address requirements:</dt>
                <dd>
                  This telephone number requires a {addressRequirement} address
                  in{' '}
                  {addressRequirement === 'local' && (
                    <>
                      <span className="text-capitalize">
                        {selectedGroup.city_name &&
                          selectedGroup.city_name.toLowerCase()}
                      </span>{' '}
                      (area code {selectedGroup.area_code}),{' '}
                    </>
                  )}
                  <span className="text-capitalize">
                    {groupCountry?.country?.toLowerCase()}
                  </span>{' '}
                  before the number can be used.
                </dd>
              </>
            )}
          </dl>
          {this.renderNameField()}
        </div>
      </ConfirmModal>
    );
  }

  renderBuyNumberModal() {
    const { selectedNumber } = this.state;
    const { phone_number: { e164_format = '' } = {} } = selectedNumber || {};
    const {
      phoneNumbers: { purchaseAsyncStatus },
    } = this.props;

    const isLoading = purchaseAsyncStatus !== 'succeeded';
    const prettyNumber = formatInternational(e164_format) || e164_format;

    return (
      <ConfirmModal
        isShown={true}
        headline={`You're about to buy ${prettyNumber}`}
        confirmText="Buy"
        confirmVariant="primary"
        confirmProps={{ isLoading, disabled: !this.isNameValid() }}
        confirm={this.handleBuyNumber}
        cancel={this.closeConfirmPurchase}
      >
        <div className={styles.confirm}>
          <div className={styles.confirmText}>
            Give this number a name so everyone on your team knows what kind of
            support this phone number will be used for.
          </div>
          {this.renderNameField()}
        </div>
      </ConfirmModal>
    );
  }

  render() {
    const {
      isOpen,
      phoneSearch,
      availableCountries: { items: countries },
    } = this.props;
    const {
      isConfirmNumberOpen,
      isConfirmGroupOpen,
      showTabs,
      selectedCountry,
    } = this.state;

    const isSubdialogOpen = isConfirmNumberOpen || isConfirmGroupOpen;

    return (
      <div>
        <Modal
          isOpen={isOpen && !isSubdialogOpen}
          onClose={this.handleModalClose}
        >
          <Box id="buy-number-modal">
            <ModalHead title="Buy a number" onClose={this.handleModalClose} />
            <ModalBody>
              <div className={styles.modalBody}>
                <CountrySelect
                  setShowTabs={this.setShowTabs}
                  phoneSearch={phoneSearch}
                  onSelect={this.setSelectedCountry}
                  countries={countries}
                  selectedCountry={selectedCountry}
                />
                {showTabs && this.renderTabs()}
                {showTabs && this.renderCurrentTab()}
              </div>
            </ModalBody>
            <ModalFoot>
              <div className="buttons">
                <Button
                  type="button"
                  variant="tertiary"
                  className="pull-left"
                  disabled={status === 'loading'}
                  onClick={this.handleModalClose}
                >
                  Cancel
                </Button>
              </div>
            </ModalFoot>
          </Box>
        </Modal>
        {isConfirmGroupOpen && this.renderBuyGroupModal()}
        {isConfirmNumberOpen && this.renderBuyNumberModal()}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  numberBuyCanceled: () =>
    dispatch({ type: 'TELEPHONY_HIDE_SEARCH_NUMBER_MODAL' }),
  numberCountrySelected: () =>
    dispatch({ type: 'TELEPHONY_BUY_NUMBER_COUNTRY_SELECTED' }),
  numberSelected: () =>
    dispatch({ type: 'TELEPHONY_BUY_NUMBER_OPTION_SELECTED' }),
  numberPurchased: () => dispatch({ type: 'TELEPHONY_BUY_NUMBER_PURCHASED' }),
});

export type BuyNumberDispatch = ReturnType<typeof mapDispatchToProps>;

export default connect<void, BuyNumberDispatch>(
  null,
  mapDispatchToProps,
)(withPhoneNumbers(withPhoneSearch(withAvailableCountries(BuyNumber))));
